/**
 * RitmonitorLiveCard — Dashboard'a canlı PostNL Ritmonitor ilerleme kartı.
 *
 * Backend: GET /api/postnl/ritmonitor → tabs[] (her firma için 1 tab)
 *   Her tab: { credential_id, credential_label, snapshot: { rides[], captured_at } }
 *   rides[]: { aantal_stops, stops_te_doen, ... }
 *
 * Hesaplanan metrikler:
 *   - total_stops    = Σ aantal_stops
 *   - remaining      = Σ stops_te_doen
 *   - completed      = total_stops - remaining
 *   - completion_pct = completed / total_stops * 100
 *   - ride_count     = rides.length
 *
 * Davranış:
 *   - 30sn'de bir auto-refresh (background cron 5dk'da bir scraper çalıştırıyor)
 *   - Değişen değerler için "flash" animasyonu (yeşil parla + arrow)
 *   - Sıfır gelen firmalar gizlenir
 */
import { useEffect, useState, useRef } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { api } from '../../lib/auth';
import { Activity, TrendingDown, CheckCircle2, RefreshCw } from 'lucide-react';

function fmtRelative(iso) {
  if (!iso) return '—';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const sec = Math.round(diff / 1000);
    if (sec < 60) return `${sec}sn önce`;
    if (sec < 3600) return `${Math.round(sec / 60)}dk önce`;
    if (sec < 86400) return `${Math.round(sec / 3600)}sa önce`;
    return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function summarize(snapshot) {
  if (!snapshot) return null;
  const rides = snapshot.rides || [];
  const totalStops = rides.reduce((s, r) => s + (Number(r.aantal_stops) || 0), 0);
  const remaining = rides.reduce((s, r) => s + (Number(r.stops_te_doen) || 0), 0);
  const completed = Math.max(0, totalStops - remaining);
  const pct = totalStops > 0 ? Math.round((completed / totalStops) * 100) : 0;
  return {
    ride_count: rides.length,
    total_stops: totalStops,
    completed,
    remaining,
    completion_pct: pct,
    captured_at: snapshot.captured_at,
  };
}

export default function RitmonitorLiveCard() {
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevRef = useRef({}); // {cred_id: prev_completed} — değişim algılama

  const load = async () => {
    try {
      const res = await api.get('/postnl/ritmonitor');
      setTabs(res.data?.data?.tabs || []);
    } catch { /* silent — dashboard ana akışı bozmasın */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  // Filtre: snapshot'ı olmayan firmaları gizle
  const visible = tabs
    .map(t => ({ ...t, summary: summarize(t.snapshot) }))
    .filter(t => t.summary && t.summary.ride_count > 0);

  if (loading || visible.length === 0) return null;

  return (
    <Card className="p-4 bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-indigo-950/30 dark:via-slate-900 dark:to-violet-950/30 border-indigo-200 dark:border-indigo-800/40" data-testid="ritmonitor-live-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Activity className="h-5 w-5 text-indigo-600" />
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 animate-ping" />
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500" />
          </div>
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            PostNL Ritmonitor — Canlı İlerleme
          </h2>
        </div>
        <button onClick={load} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-1" data-testid="ritmonitor-refresh-btn">
          <RefreshCw className="h-3 w-3" /> Şimdi yenile
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {visible.map(({ credential_id, credential_label, summary }) => {
          const prev = prevRef.current[credential_id];
          const delta = prev !== undefined ? summary.completed - prev : 0;
          // Update ref after render (within effect would cause re-render loop)
          prevRef.current[credential_id] = summary.completed;
          return (
            <FirmProgressCard
              key={credential_id}
              label={credential_label}
              summary={summary}
              delta={delta}
            />
          );
        })}
      </div>
      <div className="text-[10px] text-slate-500 mt-3 text-center">
        ⚡ 30sn'de bir otomatik yenilenir · Backend scraper her 5dk'da bir PostNL'den günceller
      </div>
    </Card>
  );
}

function FirmProgressCard({ label, summary, delta }) {
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (delta > 0) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1800);
      return () => clearTimeout(t);
    }
  }, [delta]);

  const pct = summary.completion_pct;
  const pctColor = pct >= 90 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-indigo-600';
  const barColor = pct >= 90 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-indigo-500';

  return (
    <div
      className={`relative bg-white dark:bg-slate-800/70 rounded-xl p-3 border transition-all duration-300 ${flash ? 'border-emerald-400 ring-2 ring-emerald-200 dark:ring-emerald-900/50 scale-[1.02]' : 'border-slate-200 dark:border-slate-700/50'}`}
      data-testid={`ritmonitor-firm-card-${label}`}
    >
      {flash && (
        <div className="absolute -top-2 -right-2 z-10 animate-bounce">
          <Badge className="bg-emerald-500 text-white text-[10px] shadow-md flex items-center gap-1">
            <TrendingDown className="h-2.5 w-2.5 rotate-180" />
            +{delta} yeni
          </Badge>
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{label}</span>
        <span className={`text-xl font-bold tabular-nums ${pctColor}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
          %{pct}
        </span>
      </div>
      {/* Progress bar */}
      <div className="relative h-2.5 bg-slate-100 dark:bg-slate-700/40 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full ${barColor} transition-all duration-700 ease-out rounded-full relative`}
          style={{ width: `${pct}%` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
        </div>
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <div className="flex gap-2.5 text-slate-600 dark:text-slate-300">
          <span><b className="text-slate-900 dark:text-slate-100">{summary.completed}</b>/{summary.total_stops} stops</span>
          {summary.remaining > 0 && (
            <span className="text-amber-600">{summary.remaining} kaldı</span>
          )}
          {summary.remaining === 0 && (
            <span className="text-emerald-600 flex items-center gap-0.5">
              <CheckCircle2 className="h-3 w-3" /> Tamam
            </span>
          )}
        </div>
        <span className="text-[10px] text-slate-400">{fmtRelative(summary.captured_at)}</span>
      </div>
      <div className="text-[10px] text-slate-500 mt-1">
        {summary.ride_count} aktif rota
      </div>
    </div>
  );
}
